import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose } from 'ramda';
import { Layout } from 'layouts';
import { Huggg, HugggLife, Reaction } from 'components';
import { ExpiredError } from 'components/Error/Custom';
import { PageTitle } from 'components/misc';
import { PoweredByHuggg as UIPoweredByHuggg } from 'components/ui';
import { withHuggg, withRedirect } from 'hoc';
import { useAnalytics, usePersistedState } from 'hooks';
import {
  useThemeContext,
  Divider,
  SuccessAlert
} from '@huggg/react-components';
import styles from './redeemed.module.scss';
import { isInIframe } from 'utilities/iframes';

export const ScreensRedeemed = props => {
  /**
   * Threshold at which a user us classes as "experienced" so then we toggle
   * interface elements on for them.
   *
   * @var {Integer} interfaceThreshold
   */
  const interfaceThreshold = 2;
  const { huggg, isBlueHuggg, isFirstRedeemed } = props;
  const analytics = useAnalytics();
  const [viewedShortcodes, setViewedShortcodes] = usePersistedState(
    'viewedShortcodes',
    []
  );

  const { shouldShowPoweredByHuggg, name: theme } = useThemeContext();

  const shouldShowHugggLife =
    (!theme || theme === 'default') && !isBlueHuggg && !isInIframe();

  useEffect(() => {
    const shortcode = huggg.shortcode;
    const shouldStore =
      !viewedShortcodes.includes(shortcode) &&
      viewedShortcodes.length <= interfaceThreshold;

    isFirstRedeemed
      ? analytics.screenViewedHugggRedeemed()
      : analytics.screenViewedHugggRedeemedExpired();

    if (shouldStore) {
      setViewedShortcodes([...viewedShortcodes, shortcode]);
    }
  }, [
    analytics,
    huggg.shortcode,
    isFirstRedeemed,
    setViewedShortcodes,
    viewedShortcodes
  ]);

  if (!isFirstRedeemed) {
    return (
      <Layout showBackButton={false}>
        <ExpiredError huggg={huggg} isBlueHuggg={isBlueHuggg} />
      </Layout>
    );
  }

  const shouldShowReaction = !huggg.hasReaction;

  return (
    <Layout showBackButton={false} className={styles.redeemedTick}>
      <PageTitle huggg={huggg} />
      <div
        className={classnames('container', styles.container)}
        data-test-id="redeemed-wrapper"
      >
        <Huggg huggg={huggg} />

        {shouldShowPoweredByHuggg && <UIPoweredByHuggg />}
      </div>
      <Divider />
      <div style={{ width: '320px', margin: '0 auto' }}>
        <SuccessAlert className={styles.alert} title="Order confirmed" />

        {shouldShowReaction && (
          <Reaction includeCommentSection={true} isDialog />
        )}
      </div>

      {shouldShowHugggLife && <HugggLife />}
    </Layout>
  );
};

ScreensRedeemed.propTypes = {
  huggg: PropTypes.object.isRequired,
  isBlueHuggg: PropTypes.bool.isRequired,
  isFirstRedeemed: PropTypes.bool.isRequired,
  isRedeemed: PropTypes.bool.isRequired,
  isTestHuggg: PropTypes.bool.isRequired,
  isMultiRedeemable: PropTypes.bool.isRequired,
  sender: PropTypes.object.isRequired
};

const redirectHandler = props => {
  const { isRedeemed, isMultiRedeemable } = props;

  const shouldRedirect = isMultiRedeemable || !isRedeemed;

  return [shouldRedirect, `/${props.huggg.shortcode}/redeem`];
};

export default compose(
  withHuggg,
  withRedirect(redirectHandler)
)(ScreensRedeemed);
